import { useCallback, useEffect, useState } from 'react';
import axios from '../../../axiosConfig';

/**
 * Fetches products approaching their expiry date within the given
 * window (default 60 days). Returns the bucketed counts the dashboard
 * tile also uses, plus per-item details with daysToExpiry, urgency
 * ('URGENT' | 'WARNING' | 'INFO'), and suggestedDiscountPct.
 *
 * Pass `enabled: false` to skip fetching entirely — used by the
 * Dashboard tile so merchants without `finance:view` don't fire a
 * wasted 403'd request on every dashboard load.
 */
export default function useExpiringProducts({ days = 60, enabled = true } = {}) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ urgent: 0, warning: 0, info: 0, total: 0 });
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const fetchExpiring = useCallback(async (d) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/finance/products/expiring', {
        params: { days: d },
      });
      setItems(res.data?.items || []);
      setCounts(res.data?.counts || { urgent: 0, warning: 0, info: 0, total: 0 });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load expiring stock';
      setError(msg);
      setItems([]);
      setCounts({ urgent: 0, warning: 0, info: 0, total: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchExpiring(days);
  }, [days, enabled, fetchExpiring]);

  /**
   * Apply a markdown to one product. Returns the updated product on
   * success — including `variantsDiscounted` (count of variant rows
   * the cascade touched, 0 for non-variant products). Caller is
   * responsible for refetching the list so the new price (and the
   * now-stamped compareAtPrice on parent + each variant) reflect in
   * the UI.
   */
  const applyDiscount = useCallback(async (productId, discountPct) => {
    const res = await axios.post(`/finance/products/${productId}/discount-near-expiry`, {
      discountPct,
    });
    return res.data?.product;
  }, []);

  return {
    items,
    counts,
    loading,
    error,
    refetch: () => fetchExpiring(days),
    applyDiscount,
  };
}
